import { useState, useEffect, useCallback } from "react";
import { Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, useDroppable, DragOverlay, rectIntersection, pointerWithin } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { format, parseISO, addDays, startOfWeek, addWeeks } from "date-fns";
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Plus, Factory, Calendar, Package, Clock, TrendingUp, AlertTriangle, User, LogOut, Box, Warehouse, ShoppingCart, BarChart3, CalendarClock, Briefcase, FlaskConical, Leaf, ArrowRightLeft } from "lucide-react";
import WorkOrderCard from "./components/WorkOrderCard";
import WorkOrderDetailModal from "./components/WorkOrderDetailModal";
import ProductionDashboard from "@/pages/ProductionDashboard";
import PalletLogDashboard from "@/pages/PalletLogDashboard";
// CRM Pages
import { CRMDashboard, OpportunitiesPage as CRMOpportunitiesPage, OpportunityDetailPage, ActionItemsPage as CRMActionItemsPage, MeetingsPage as CRMMeetingsPage, CustomersPage, CustomerDetailPage, BrokersPage } from "@/pages/crm";
// R&D Pages
import { RDDashboard, RDProjectsPage, RDProjectDetailPage, RDItemDetailPage, RDPendingItemsPage, RDNutritionPage } from "@/pages/rd";
import ItemsPage from "@/pages/ItemsPage";
import ItemDetailPage from "@/pages/ItemDetailPage";
import WorkOrderDetailPage from "@/pages/WorkOrderDetailPage";
import PlanningDashboard from "@/pages/PlanningDashboard";
import PlanningRawMaterialsPage from "@/pages/PlanningRawMaterialsPage";
import PlanningFinishedGoodsPage from "@/pages/PlanningFinishedGoodsPage";
import PurchaseOrdersPage from "@/pages/PurchaseOrdersPage";
import PurchaseOrderDetailPage from "@/pages/PurchaseOrderDetailPage";
import SalesOrdersPage from "@/pages/SalesOrdersPage";
import SalesOrderDetailPage from "@/pages/SalesOrderDetailPage";
import WarehousePage from "@/pages/WarehousePage";
import StorageMapPage from "@/pages/StorageMapPage";
import TrucksPage from "@/pages/TrucksPage";
import TruckDetailPage from "@/pages/TruckDetailPage";
import HoldsPage from "@/pages/HoldsPage";
import TransactionsPage from "@/pages/TransactionsPage";
import ReceiptsPage from "@/pages/ReceiptsPage";
import ReceiptDetailPage from "@/pages/ReceiptDetailPage";
import MobileReceiptPage from "@/pages/MobileReceiptPage";
import LicensePlateScannerPage from "@/pages/LicensePlateScannerPage";
import InternalTransfersPage from "@/pages/InternalTransfersPage";
import TransferDetailPage from "@/pages/TransferDetailPage";
import TransferAddItemsPage from "@/pages/TransferAddItemsPage";
import QAOverridePage from "@/pages/QAOverridePage";
import FacilityMapPage from "@/pages/FacilityMapPage";
import SettingsPage from "@/pages/SettingsPage";
// Mobile App Pages
import { MobileLayout, MobileDashboard, MobileProductionPage, MobileWorkOrderDetail, MobileWarehousePage, MobileScannerPage, MobileSchedulePage, MobileMorePage, MobileRDPage, MobileCRMPage, MobileWarehouseReceivePage, MobileWarehouseHoldsPage, MobileWarehouseAllocatePage, MobileWarehouseTransferPage, MobileWarehouseTrucksPage, MobileWarehouseWOListPage, MobileWarehouseWODetailPage, MobileItemsPage, MobileItemDetailPage, MobilePurchaseOrdersPage, MobilePODetailPage, MobileSalesOrdersPage, MobileSODetailPage, MobileCRMOpportunitiesPage, MobileCRMOpportunityDetailPage, MobileCRMCustomersPage, MobileCRMCustomerDetailPage, MobileCRMBrokersPage, MobileRDPendingItemsPage, MobileRDProjectDetailPage, MobileRDItemDetailPage, MobilePlanningPage, MobileSettingsPage } from "@/pages/mobile";
import { BACKEND_URL, API, WS_URL } from "@/config";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import ProtectedRoute from "@/components/ProtectedRoute";
import { useAuth } from "@/contexts/AuthContext";

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 0 })); // 0 = Sunday
  const [ws, setWs] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [filterLines, setFilterLines] = useState(["P1", "P2", "P3"]); // Multi-select for lines
  const [filterCategory, setFilterCategory] = useState("all");
  const [viewMode, setViewMode] = useState("week"); // week, month, max, custom
  const [clonedWorkOrder, setClonedWorkOrder] = useState(null);
  const [sideNavCollapsed, setSideNavCollapsed] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [customEndDate, setCustomEndDate] = useState(format(addDays(new Date(), 7), "yyyy-MM-dd"));
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  
  // Submenu expansion state
  const [expandedMenus, setExpandedMenus] = useState({
    operations: true,
    inventory: true,
    warehouse: true,
    crm: true,
    rd: true
  });
  
  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };
  
  // Create work order form state
  const [newWO, setNewWO] = useState({
    production_line: "P1",
    item_id: "",
    item_name: "",
    planned_date: format(new Date(), "yyyy-MM-dd"),
    planned_start_time: "08:00",
    planned_end_time: "16:00",
    planned_quantity: "",
    notes: ""
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  // Custom collision detection that prioritizes droppable day columns
  const customCollisionDetection = (args) => {
    // First, check for pointer intersections with droppable zones
    const pointerCollisions = pointerWithin(args);
    
    // Filter to only droppable day columns (not individual work orders)
    const droppableCollisions = pointerCollisions.filter(collision => {
      return collision.id.toString().startsWith('droppable-');
    });
    
    // If we found droppable day columns, use those
    if (droppableCollisions.length > 0) {
      return droppableCollisions;
    }
    
    // Otherwise fall back to rectangle intersection
    const rectCollisions = rectIntersection(args);
    return rectCollisions.filter(collision => {
      return collision.id.toString().startsWith('droppable-');
    });
  };

  // WebSocket connection
  useEffect(() => {
    const connectWebSocket = () => {
      const websocket = new WebSocket(`${WS_URL}/api/ws`);
      
      websocket.onopen = () => {
        console.log("WebSocket connected");
        toast.success("Real-time updates enabled");
      };
      
      websocket.onmessage = (event) => {
        const message = JSON.parse(event.data);
        console.log("WebSocket message received:", message);
        console.log("Message type:", message.type);
        
        switch (message.type) {
          case "work_order_created":
            console.log("Handling work_order_created, adding to state:", message.data);
            setWorkOrders(prev => {
              console.log("Previous work orders:", prev.length);
              const updated = [...prev, message.data];
              console.log("Updated work orders:", updated.length);
              return updated;
            });
            toast.success("New work order created");
            break;
          case "work_order_updated":
            setWorkOrders(prev => prev.map(wo => 
              wo.wo_id === message.data.wo_id ? message.data : wo
            ));
            toast.info("Work order updated");
            break;
          case "work_order_rescheduled":
            setWorkOrders(prev => prev.map(wo => 
              wo.wo_id === message.data.wo_id ? message.data : wo
            ));
            toast.info("Work order rescheduled");
            break;
          case "work_order_deleted":
            setWorkOrders(prev => prev.filter(wo => wo.wo_id !== message.data.wo_id));
            toast.info("Work order deleted");
            break;
          default:
            break;
        }
      };
      
      websocket.onerror = (error) => {
        console.error("WebSocket error:", error);
      };
      
      websocket.onclose = () => {
        console.log("WebSocket disconnected, reconnecting...");
        setTimeout(connectWebSocket, 3000);
      };
      
      setWs(websocket);
    };
    
    connectWebSocket();
    
    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, []);

  // Fetch initial data
  useEffect(() => {
    fetchWorkOrders();
    fetchItems();
  }, []);

  // Persist and restore current page
  useEffect(() => {
    // Restore from localStorage on mount
    const savedPath = localStorage.getItem('rudder_current_path');
    if (savedPath && location.pathname === '/') {
      navigate(savedPath, { replace: true });
    }
  }, []);

  useEffect(() => {
    // Save to localStorage whenever location changes
    if (location.pathname !== '/') {
      localStorage.setItem('rudder_current_path', location.pathname);
    }
  }, [location.pathname]);

  const fetchWorkOrders = async () => {
    try {
      console.log("Fetching work orders from:", `${API}/work-orders`);
      const response = await axios.get(`${API}/work-orders`);
      console.log("Work orders received:", response.data?.length || 0, "items");
      setWorkOrders(response.data || []);
    } catch (error) {
      console.error("Error fetching work orders:", error);
      console.error("Error details:", error.response?.data || error.message);
      toast.error("Failed to load work orders");
      setWorkOrders([]); // Set empty array on error
    }
  };

  const fetchItems = async () => {
    try {
      console.log("Fetching items from:", `${API}/items`);
      const response = await axios.get(`${API}/items`);
      console.log("Items received:", response.data?.length || 0, "items");
      setItems(response.data || []);
    } catch (error) {
      console.error("Error fetching items:", error);
      console.error("Error details:", error.response?.data || error.message);
      toast.error("Failed to load items");
      setItems([]); // Set empty array on error
    }
  };

  const handleCreateWorkOrder = async () => {
    if (!newWO.item_id || !newWO.planned_quantity) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const selectedItem = items.find(item => item.item_id === newWO.item_id);
      const payload = {
        ...newWO,
        item_name: selectedItem?.name || "",
        planned_quantity: parseFloat(newWO.planned_quantity)
      };

      await axios.post(`${API}/work-orders`, payload);
      toast.success("Work order created successfully");
      setShowCreateModal(false);
      setNewWO({
        production_line: "P1",
        item_id: "",
        item_name: "",
        planned_date: format(new Date(), "yyyy-MM-dd"),
        planned_start_time: "08:00",
        planned_end_time: "16:00",
        planned_quantity: "",
        notes: ""
      });
    } catch (error) {
      console.error("Error creating work order:", error);
      toast.error("Failed to create work order");
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    setActiveId(null);
    
    if (!over) return;
    
    // Extract date from droppable ID (format: "droppable-YYYY-MM-DD")
    const overId = over.id.toString();
    const parts = overId.split("-");
    
    if (parts[0] !== "droppable") return;
    
    const newDate = `${parts[1]}-${parts[2]}-${parts[3]}`;
    const workOrder = workOrders.find(wo => wo.wo_id === active.id);
    
    if (!workOrder || workOrder.planned_date === newDate) return;
    
    // Optimistically update the UI immediately
    setWorkOrders(prev => prev.map(wo => 
      wo.wo_id === active.id 
        ? { ...wo, planned_date: newDate }
        : wo
    ));
    
    try {
      await axios.put(`${API}/work-orders/${active.id}/reschedule`, {
        new_date: newDate
      });
      toast.success("Work order rescheduled");
      // WebSocket will sync with other clients
    } catch (error) {
      console.error("Error rescheduling work order:", error);
      toast.error("Failed to reschedule work order");
      // Revert on error
      fetchWorkOrders();
    }
  };

  const handleWorkOrderClick = (workOrder) => {
    // For in_progress or completed work orders, navigate to detail page
    if (workOrder.status === 'in_progress' || workOrder.status === 'completed') {
      navigate(`/work-order/${workOrder.wo_id}?from=schedule`);
    } else {
      // For scheduled work orders, open the modal
      setSelectedWorkOrder(workOrder);
      setShowDetailModal(true);
    }
  };

  const handleUpdateWorkOrder = async (updatedData) => {
    if (!selectedWorkOrder) return;
    
    try {
      await axios.put(`${API}/work-orders/${selectedWorkOrder.wo_id}`, updatedData);
      toast.success("Work order updated successfully");
      // WebSocket will handle the state update
    } catch (error) {
      console.error("Error updating work order:", error);
      toast.error("Failed to update work order");
    }
  };

  const handleDeleteWorkOrder = async (woId) => {
    try {
      await axios.delete(`${API}/work-orders/${woId}`);
      toast.success("Work order deleted successfully");
      setShowDetailModal(false);
      // WebSocket will handle the state update
    } catch (error) {
      console.error("Error deleting work order:", error);
      toast.error("Failed to delete work order");
    }
  };

  const handleCloneWorkOrder = (workOrder) => {
    setClonedWorkOrder(workOrder);
    toast.success("Work order copied. Click on a day to paste.");
    setShowDetailModal(false);
  };

  // Generate days based on view mode
  const getDaysForView = () => {
    switch (viewMode) {
      case "week":
        return Array.from({ length: 7 }, (_, i) => addDays(currentWeek, i));
      case "month":
        // Show 4 weeks (28 days) starting from currentWeek
        return Array.from({ length: 28 }, (_, i) => addDays(currentWeek, i));
      case "max":
        // Current week minus 2 weeks, plus 13 weeks = 16 weeks total (112 days)
        const startDate = addWeeks(currentWeek, -2);
        return Array.from({ length: 112 }, (_, i) => addDays(startDate, i));
      case "custom":
        // Custom date range
        const start = parseISO(customStartDate);
        const end = parseISO(customEndDate);
        const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1; // +1 to include end date
        return Array.from({ length: days }, (_, i) => addDays(start, i));
      default:
        return Array.from({ length: 7 }, (_, i) => addDays(currentWeek, i));
    }
  };
  
  const weekDays = getDaysForView();

  // Toggle production line filter
  const toggleLineFilter = (line) => {
    setFilterLines(prev => {
      if (prev.includes(line)) {
        return prev.filter(l => l !== line);
      } else {
        return [...prev, line];
      }
    });
  };

  // Apply filters to work orders
  const filteredWorkOrders = workOrders.filter(wo => {
    const lineMatch = filterLines.includes(wo.production_line);
    const categoryMatch = filterCategory === "all" || 
      items.find(item => item.item_id === wo.item_id)?.category === filterCategory;
    return lineMatch && categoryMatch;
  });

  // Group work orders by date, sorted by production line then start time
  const getWorkOrdersForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return filteredWorkOrders
      .filter(wo => wo.planned_date === dateStr)
      .sort((a, b) => {
        // First sort by production line (P1, P2, P3)
        const lineCompare = a.production_line.localeCompare(b.production_line);
        if (lineCompare !== 0) return lineCompare;
        
        // Then sort by start time within the same line
        if (a.planned_start_time && b.planned_start_time) {
          return a.planned_start_time.localeCompare(b.planned_start_time);
        }
        return 0;
      });
  };

  // Droppable zone component (single calendar for all lines)
  const DroppableDay = ({ date, children }) => {
    const droppableId = `droppable-${format(date, "yyyy-MM-dd")}`;
    const { setNodeRef, isOver } = useDroppable({
      id: droppableId,
      data: {
        type: 'day-column',
        date: format(date, "yyyy-MM-dd")
      }
    });

    // Check if this is today
    const today = format(new Date(), "yyyy-MM-dd");
    const isToday = format(date, "yyyy-MM-dd") === today;

    const handleDayClick = async (e) => {
      // Only open modal if clicking on empty space (not on work order cards)
      if (e.target.closest('[data-testid^="work-order-card-"]')) {
        return;
      }
      
      // If there's a cloned work order, paste it
      if (clonedWorkOrder) {
        try {
          const pasteDate = format(date, "yyyy-MM-dd");
          const payload = {
            production_line: clonedWorkOrder.production_line,
            item_id: clonedWorkOrder.item_id,
            item_name: clonedWorkOrder.item_name,
            planned_date: pasteDate,
            planned_start_time: clonedWorkOrder.planned_start_time,
            planned_end_time: clonedWorkOrder.planned_end_time,
            planned_quantity: clonedWorkOrder.planned_quantity,
            notes: clonedWorkOrder.notes
          };
          await axios.post(`${API}/work-orders`, payload);
          toast.success(`Work order pasted to ${format(date, "MMM d")}`);
          setClonedWorkOrder(null);
        } catch (error) {
          console.error("Error pasting work order:", error);
          toast.error("Failed to paste work order");
        }
        return;
      }
      
      // Open create modal and pre-fill the date
      setNewWO(prev => ({
        ...prev,
        planned_date: format(date, "yyyy-MM-dd")
      }));
      setShowCreateModal(true);
    };

    return (
      <div 
        ref={setNodeRef}
        className={`day-column ${isOver ? 'day-column-over' : ''} ${isToday ? 'day-column-today' : ''}`}
        data-testid={`day-column-${format(date, "yyyy-MM-dd")}`}
        style={{ position: 'relative' }}
        onClick={handleDayClick}
      >
        {children}
      </div>
    );
  };

  // Statistics (based on filtered work orders)
  const stats = {
    total: filteredWorkOrders.length,
    scheduled: filteredWorkOrders.filter(wo => wo.status === "scheduled").length,
    inProgress: filteredWorkOrders.filter(wo => wo.status === "in_progress").length,
    completed: filteredWorkOrders.filter(wo => wo.status === "completed").length
  };

  // Get unique categories from items
  const categories = [...new Set(items.map(item => item.category))].sort();

  const isMobileRoute = location.pathname.startsWith('/mobile');
  const { user, logout } = useAuth();

  // Public auth routes (no protection)
  if (location.pathname === "/login" || location.pathname === "/signup") {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
      </Routes>
    );
  }

  // Protected app (desktop + mobile)
  return (
    <ProtectedRoute>
      {/* Mobile App - completely separate layout */}
      {isMobileRoute ? (
      <div className="App">
        <Toaster position="top-right" />
        <Routes>
          <Route path="/mobile" element={<MobileLayout />}>
            <Route index element={<MobileDashboard />} />
            <Route path="production" element={<MobileProductionPage />} />
            <Route path="production/:wo_id" element={<MobileWorkOrderDetail />} />
            <Route path="warehouse" element={<MobileWarehousePage />} />
            <Route path="warehouse/scan" element={<MobileScannerPage />} />
            <Route path="warehouse/receive" element={<MobileWarehouseReceivePage />} />
            <Route path="warehouse/holds" element={<MobileWarehouseHoldsPage />} />
            <Route path="warehouse/allocate" element={<MobileWarehouseAllocatePage />} />
            <Route path="warehouse/transfer" element={<MobileWarehouseTransferPage />} />
            <Route path="warehouse/trucks" element={<MobileWarehouseTrucksPage />} />
            <Route path="warehouse/work-orders" element={<MobileWarehouseWOListPage />} />
            <Route path="warehouse/wo/:wo_id" element={<MobileWarehouseWODetailPage />} />
            <Route path="schedule" element={<MobileSchedulePage />} />
            <Route path="more" element={<MobileMorePage />} />
            <Route path="rd" element={<MobileRDPage />} />
            <Route path="rd/projects" element={<MobileRDPage />} />
            <Route path="rd/projects/:projectId" element={<MobileRDProjectDetailPage />} />
            <Route path="rd/items/:itemId" element={<MobileRDItemDetailPage />} />
            <Route path="rd/pending-items" element={<MobileRDPendingItemsPage />} />
            <Route path="crm" element={<MobileCRMPage />} />
            <Route path="crm/opportunities" element={<MobileCRMOpportunitiesPage />} />
            <Route path="crm/opportunities/:oppId" element={<MobileCRMOpportunityDetailPage />} />
            <Route path="crm/customers" element={<MobileCRMCustomersPage />} />
            <Route path="crm/customers/:custId" element={<MobileCRMCustomerDetailPage />} />
            <Route path="crm/brokers" element={<MobileCRMBrokersPage />} />
            <Route path="items" element={<MobileItemsPage />} />
            <Route path="items/:itemId" element={<MobileItemDetailPage />} />
            <Route path="purchase-orders" element={<MobilePurchaseOrdersPage />} />
            <Route path="purchase-orders/:poId" element={<MobilePODetailPage />} />
            <Route path="sales-orders" element={<MobileSalesOrdersPage />} />
            <Route path="sales-orders/:soId" element={<MobileSODetailPage />} />
            <Route path="planning" element={<MobilePlanningPage />} />
            <Route path="settings" element={<MobileSettingsPage />} />
          </Route>
        </Routes>
      </div>
      ) : (
    <>
    <div className="App">
      <Toaster position="top-right" />
      
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <button 
            className="menu-toggle" 
            onClick={() => setSideNavCollapsed(!sideNavCollapsed)}
            data-testid="menu-toggle"
            title={sideNavCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sideNavCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
          <div className="header-left">
            <img 
              src="https://customer-assets.emergentagent.com/job_mfg-workorder-app/artifacts/fp1li17d_Mockingbird-Food-Group_Black.png" 
              alt="Mockingbird Food Group" 
              className="header-logo"
              style={{ backgroundColor: 'transparent' }}
            />
            <h1 className="header-title">Rudder</h1>
          </div>
          <div className="header-right">
            <button className="header-btn" data-testid="user-btn" title={user?.email}>
              <User className="w-4 h-4" />
              <span>{user?.display_name || user?.email || "User"}</span>
            </button>
            <button
              className="header-btn"
              data-testid="logout-btn"
              onClick={() => { logout(); navigate("/login"); }}
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="app-layout">
        {/* Side Navigation */}
        <aside className={`side-nav ${sideNavCollapsed ? 'side-nav-collapsed' : ''}`}>
          <nav className="nav-menu">
            {/* Operations Parent Menu */}
            <div className="nav-section">
              <button 
                className="nav-item nav-parent"
                onClick={() => toggleMenu('operations')}
                title="Operations"
              >
                <Factory className="w-4 h-4" />
                {!sideNavCollapsed && (
                  <>
                    <span>Operations</span>
                    {expandedMenus.operations ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
                  </>
                )}
              </button>
              {!sideNavCollapsed && expandedMenus.operations && (
                <div className="nav-submenu">
                  <button 
                    className={`nav-item nav-subitem ${location.pathname === "/schedule" || location.pathname === "/" ? "nav-item-active" : ""}`}
                    onClick={() => navigate("/schedule")}
                    title="Scheduling"
                  >
                    <Calendar className="w-4 h-4" />
                    <span>Scheduling</span>
                  </button>
                  <button 
                    className={`nav-item nav-subitem ${location.pathname === "/dashboard" ? "nav-item-active" : ""}`}
                    onClick={() => navigate("/dashboard")}
                    title="Dashboard"
                  >
                    <BarChart3 className="w-4 h-4" />
                    <span>Dashboard</span>
                  </button>
                </div>
              )}
            </div>

            {/* Inventory Parent Menu */}
            <div className="nav-section">
              <button 
                className="nav-item nav-parent"
                onClick={() => toggleMenu('inventory')}
                title="Inventory"
              >
                <Box className="w-4 h-4" />
                {!sideNavCollapsed && (
                  <>
                    <span>Inventory</span>
                    {expandedMenus.inventory ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
                  </>
                )}
              </button>
              {!sideNavCollapsed && expandedMenus.inventory && (
                <div className="nav-submenu">
                  <button 
                    className={`nav-item nav-subitem ${location.pathname === "/items" ? "nav-item-active" : ""}`}
                    onClick={() => navigate("/items")}
                    title="Items"
                  >
                    <Package className="w-4 h-4" />
                    <span>Items</span>
                  </button>
                  <div className="nav-subsection">
                    <button 
                      className={`nav-item nav-subitem ${location.pathname === "/planning" ? "nav-item-active" : ""}`}
                      onClick={() => navigate("/planning")}
                      title="Planning Dashboard"
                    >
                      <CalendarClock className="w-4 h-4" />
                      <span>Planning</span>
                    </button>
                    <button 
                      className={`nav-item nav-subitem-nested ${location.pathname === "/planning/raw-materials" ? "nav-item-active" : ""}`}
                      onClick={() => navigate("/planning/raw-materials")}
                      title="Raw Materials"
                    >
                      <span>Raw Materials</span>
                    </button>
                    <button 
                      className={`nav-item nav-subitem-nested ${location.pathname === "/planning/finished-goods" ? "nav-item-active" : ""}`}
                      onClick={() => navigate("/planning/finished-goods")}
                      title="Finished Goods"
                    >
                      <span>Finished Goods</span>
                    </button>
                  </div>
                  <button 
                    className={`nav-item nav-subitem ${location.pathname === "/purchase-orders" ? "nav-item-active" : ""}`}
                    onClick={() => navigate("/purchase-orders")}
                    title="Purchase Orders"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    <span>Purchase Orders</span>
                  </button>
                  <button 
                    className={`nav-item nav-subitem ${location.pathname === "/sales-orders" ? "nav-item-active" : ""}`}
                    onClick={() => navigate("/sales-orders")}
                    title="Sales Orders"
                  >
                    <TrendingUp className="w-4 h-4" />
                    <span>Sales Orders</span>
                  </button>
                  <button 
                    className={`nav-item nav-subitem ${location.pathname === "/internal-transfers" ? "nav-item-active" : ""}`}
                    onClick={() => navigate("/internal-transfers")}
                    title="Internal Transfers"
                    data-testid="nav-internal-transfers"
                  >
                    <ArrowRightLeft className="w-4 h-4" />
                    <span>Internal Transfers</span>
                  </button>
                </div>
              )}
            </div>

            {/* Warehouse Parent Menu */}
            <div className="nav-section">
              <button 
                className={`nav-item nav-parent ${location.pathname.startsWith("/warehouse") ? "nav-item-active" : ""}`}
                onClick={() => toggleMenu('warehouse')}
                title="Warehouse"
              >
                <Warehouse className="w-4 h-4" />
                {!sideNavCollapsed && (
                  <>
                    <span>Warehouse</span>
                    {expandedMenus.warehouse ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
                  </>
                )}
              </button>

              {/* Warehouse Submenu */}
              {!sideNavCollapsed && expandedMenus.warehouse && (
                <div className="nav-submenu">
                  <button 
                    className={`nav-item nav-child ${location.pathname === "/warehouse" ? "nav-item-active" : ""}`}
                    onClick={() => navigate("/warehouse")}
                    title="Dashboard"
                  >
                    <BarChart3 className="w-4 h-4" />
                    <span>Dashboard</span>
                  </button>
                  <button 
                    className={`nav-item nav-child ${location.pathname === "/warehouse/storage-map" ? "nav-item-active" : ""}`}
                    onClick={() => navigate("/warehouse/storage-map")}
                    title="Storage Map"
                  >
                    <Box className="w-4 h-4" />
                    <span>Storage Map</span>
                  </button>
                </div>
              )}
            </div>

            {/* CRM Parent Menu */}
            <div className="nav-section">
              <button 
                className={`nav-item nav-parent ${location.pathname.startsWith("/crm") ? "nav-item-active" : ""}`}
                onClick={() => toggleMenu('crm')}
                title="CRM"
              >
                <Briefcase className="w-4 h-4" />
                {!sideNavCollapsed && (
                  <>
                    <span>CRM</span>
                    {expandedMenus.crm ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
                  </>
                )}
              </button>

              {/* CRM Submenu */}
              {!sideNavCollapsed && expandedMenus.crm && (
                <div className="nav-submenu">
                  <button 
                    className={`nav-item nav-child ${location.pathname === "/crm" || location.pathname === "/crm/dashboard" ? "nav-item-active" : ""}`}
                    onClick={() => navigate("/crm")}
                    title="CRM Dashboard"
                  >
                    <BarChart3 className="w-4 h-4" />
                    <span>Dashboard</span>
                  </button>
                  <button 
                    className={`nav-item nav-child ${location.pathname.startsWith("/crm/opportunities") ? "nav-item-active" : ""}`}
                    onClick={() => navigate("/crm/opportunities")}
                    title="Opportunities"
                  >
                    <TrendingUp className="w-4 h-4" />
                    <span>Opportunities</span>
                  </button>
                  <button 
                    className={`nav-item nav-child ${location.pathname === "/crm/customers" ? "nav-item-active" : ""}`}
                    onClick={() => navigate("/crm/customers")}
                    title="Customers"
                  >
                    <User className="w-4 h-4" />
                    <span>Customers</span>
                  </button>
                  <button 
                    className={`nav-item nav-child ${location.pathname === "/crm/action-items" ? "nav-item-active" : ""}`}
                    onClick={() => navigate("/crm/action-items")}
                    title="Action Items"
                  >
                    <Package className="w-4 h-4" />
                    <span>Action Items</span>
                  </button>
                  <button 
                    className={`nav-item nav-child ${location.pathname === "/crm/meetings" ? "nav-item-active" : ""}`}
                    onClick={() => navigate("/crm/meetings")}
                    title="Meetings"
                  >
                    <Calendar className="w-4 h-4" />
                    <span>Meetings</span>
                  </button>
                  <button 
                    className={`nav-item nav-child ${location.pathname === "/crm/brokers" ? "nav-item-active" : ""}`}
                    onClick={() => navigate("/crm/brokers")}
                    title="Brokers"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    <span>Brokers</span>
                  </button>
                </div>
              )}
            </div>

            {/* R&D Parent Menu */}
            <div className="nav-section">
              <button 
                className={`nav-item nav-parent ${location.pathname.startsWith("/rd") ? "nav-item-active" : ""}`}
                onClick={() => toggleMenu('rd')}
                title="R&D"
              >
                <FlaskConical className="w-4 h-4" />
                {!sideNavCollapsed && (
                  <>
                    <span>R&D</span>
                    {expandedMenus.rd ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
                  </>
                )}
              </button>

              {/* R&D Submenu */}
              {!sideNavCollapsed && expandedMenus.rd && (
                <div className="nav-submenu">
                  <button 
                    className={`nav-item nav-child ${location.pathname === "/rd" || location.pathname === "/rd/dashboard" ? "nav-item-active" : ""}`}
                    onClick={() => navigate("/rd")}
                    title="R&D Dashboard"
                  >
                    <BarChart3 className="w-4 h-4" />
                    <span>Dashboard</span>
                  </button>
                  <button 
                    className={`nav-item nav-child ${location.pathname.startsWith("/rd/projects") ? "nav-item-active" : ""}`}
                    onClick={() => navigate("/rd/projects")}
                    title="Projects"
                  >
                    <FlaskConical className="w-4 h-4" />
                    <span>Projects</span>
                  </button>
                  <button 
                    className={`nav-item nav-child ${location.pathname === "/rd/pending-items" ? "nav-item-active" : ""}`}
                    onClick={() => navigate("/rd/pending-items")}
                    title="Pending Items"
                  >
                    <Package className="w-4 h-4" />
                    <span>Pending Items</span>
                  </button>
                  <button 
                    className={`nav-item nav-child ${location.pathname === "/rd/nutrition" ? "nav-item-active" : ""}`}
                    onClick={() => navigate("/rd/nutrition")}
                    title="Nutrition Database"
                  >
                    <Leaf className="w-4 h-4" />
                    <span>Nutrition</span>
                  </button>
                </div>
              )}
            </div>

            {/* Existing menu items */}
            <button 
              className={`nav-item ${location.pathname === "/reporting" ? "nav-item-active" : ""}`}
              onClick={() => navigate("/reporting")}
              data-testid="nav-reporting"
              title="Reporting"
            >
              <TrendingUp className="w-4 h-4" />
              {!sideNavCollapsed && <span>Reporting</span>}
            </button>
            <button 
              className={`nav-item ${location.pathname === "/settings" ? "nav-item-active" : ""}`}
              onClick={() => navigate("/settings")}
              data-testid="nav-settings"
              title="Settings"
            >
              <Clock className="w-4 h-4" />
              {!sideNavCollapsed && <span>Settings</span>}
            </button>
            <button 
              className={`nav-item ${location.pathname === "/users" ? "nav-item-active" : ""}`}
              onClick={() => navigate("/users")}
              data-testid="nav-users"
              title="Users"
            >
              <User className="w-4 h-4" />
              {!sideNavCollapsed && <span>Users</span>}
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="main-content">
          <Routes>
            <Route path="/dashboard" element={<ProductionDashboard />} />
            <Route path="/items" element={<ItemsPage />} />
            <Route path="/items/:itemId" element={<ItemDetailPage />} />
            <Route path="/planning" element={<PlanningDashboard />} />
            <Route path="/planning/raw-materials" element={<PlanningRawMaterialsPage />} />
            <Route path="/planning/finished-goods" element={<PlanningFinishedGoodsPage />} />
            <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
            <Route path="/purchase-orders/:poId" element={<PurchaseOrderDetailPage />} />
            <Route path="/sales-orders" element={<SalesOrdersPage />} />
            <Route path="/sales-orders/:soId" element={<SalesOrderDetailPage />} />
            <Route path="/internal-transfers" element={<InternalTransfersPage />} />
            <Route path="/internal-transfers/:transferId" element={<TransferDetailPage />} />
            <Route path="/internal-transfers/:transferId/add-items" element={<TransferAddItemsPage />} />
            <Route path="/internal-transfers/:transferId/qa-override" element={<QAOverridePage />} />
            <Route path="/warehouse" element={<WarehousePage />} />
            <Route path="/warehouse/storage-map" element={<FacilityMapPage />} />
            <Route path="/warehouse/trucks" element={<TrucksPage />} />
            <Route path="/warehouse/trucks/:truck_id" element={<TruckDetailPage />} />
            <Route path="/warehouse/holds" element={<HoldsPage />} />
            <Route path="/warehouse/transactions" element={<TransactionsPage />} />
            <Route path="/warehouse/receipts" element={<ReceiptsPage />} />
            <Route path="/warehouse/receipts/:receiptId" element={<MobileReceiptPage />} />
            <Route path="/warehouse/receipts/:receipt_id/desktop" element={<ReceiptDetailPage />} />
            <Route path="/warehouse/scanner" element={<LicensePlateScannerPage />} />
            <Route path="/warehouse/utilization" element={<StorageMapPage />} />
            <Route path="/operations/packing" element={<PalletLogDashboard />} />
            <Route path="/work-order/:wo_id" element={<WorkOrderDetailPage />} />
            {/* CRM Routes */}
            <Route path="/crm" element={<CRMDashboard navigateToOpportunity={(id) => navigate(`/crm/opportunities/${id}`)} navigateToOpportunitiesWithFilter={(stage) => navigate(`/crm/opportunities?stage=${stage}`)} navigateToActionItem={(id) => navigate(`/crm/action-items/${id}`)} />} />
            <Route path="/crm/dashboard" element={<CRMDashboard navigateToOpportunity={(id) => navigate(`/crm/opportunities/${id}`)} navigateToOpportunitiesWithFilter={(stage) => navigate(`/crm/opportunities?stage=${stage}`)} navigateToActionItem={(id) => navigate(`/crm/action-items/${id}`)} />} />
            <Route path="/crm/opportunities" element={<CRMOpportunitiesPage navigateToOpportunity={(id) => navigate(`/crm/opportunities/${id}`)} />} />
            <Route path="/crm/opportunities/:opportunityId" element={<OpportunityDetailPage />} />
            <Route path="/crm/customers" element={<CustomersPage />} />
            <Route path="/crm/customers/:customerId" element={<CustomerDetailPage />} />
            <Route path="/crm/action-items" element={<CRMActionItemsPage navigateToOpportunity={(id) => navigate(`/crm/opportunities/${id}`)} />} />
            <Route path="/crm/meetings" element={<CRMMeetingsPage navigateToOpportunity={(id) => navigate(`/crm/opportunities/${id}`)} />} />
            <Route path="/crm/brokers" element={<BrokersPage />} />
            {/* R&D Routes */}
            <Route path="/rd" element={<RDDashboard />} />
            <Route path="/rd/dashboard" element={<RDDashboard />} />
            <Route path="/rd/projects" element={<RDProjectsPage />} />
            <Route path="/rd/projects/new" element={<RDProjectsPage />} />
            <Route path="/rd/projects/:projectId" element={<RDProjectDetailPage />} />
            <Route path="/rd/items/:itemId" element={<RDItemDetailPage />} />
            <Route path="/rd/pending-items" element={<RDPendingItemsPage />} />
            <Route path="/rd/nutrition" element={<RDNutritionPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/" element={
              <>
      {/* Scheduling Grid - Single Calendar View */}
      <DndContext 
        sensors={sensors} 
        collisionDetection={customCollisionDetection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="schedule-container-single" data-testid="schedule-grid">
          <div className="calendar-header">
            <div className="calendar-header-top">
              <div className="calendar-title-section">
                <h3 className="calendar-title">
                  <Factory className="w-5 h-5 inline mr-2" />
                  Production Schedule
                </h3>
              </div>
              
              {/* View Toggle and Week Navigation */}
              <div className="view-controls">
                <div className="view-toggle">
                  <button
                    className={`view-btn ${viewMode === "week" ? "view-btn-active" : ""}`}
                    onClick={() => setViewMode("week")}
                    data-testid="view-week-btn"
                  >
                    Week
                  </button>
                  <button
                    className={`view-btn ${viewMode === "month" ? "view-btn-active" : ""}`}
                    onClick={() => setViewMode("month")}
                    data-testid="view-month-btn"
                  >
                    Month
                  </button>
                  <button
                    className={`view-btn ${viewMode === "max" ? "view-btn-active" : ""}`}
                    onClick={() => setViewMode("max")}
                    data-testid="view-max-btn"
                  >
                    Max
                  </button>
                  <button
                    className={`view-btn ${viewMode === "custom" ? "view-btn-active" : ""}`}
                    onClick={() => setShowCustomDateModal(true)}
                    data-testid="view-custom-btn"
                  >
                    Custom
                  </button>
                </div>
                
                <div className="compact-week-nav">
                  {viewMode === "custom" ? (
                    <span className="compact-week-label">
                      {customStartDate} to {customEndDate}
                    </span>
                  ) : (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setCurrentWeek(addWeeks(currentWeek, -1))}
                        className="compact-nav-btn"
                        data-testid="prev-week-btn"
                      >
                        <ChevronLeft className="w-3 h-3" />
                      </Button>
                      <span className="compact-week-label">
                        {format(currentWeek, "MMM d, yyyy")}
                      </span>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}
                        className="compact-nav-btn"
                        data-testid="next-week-btn"
                      >
                        <ChevronRight className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            {/* Combined Filters: Color Key with Multi-select + Category */}
            <div className="filters-section">
              <div className="filter-box">
                <span className="filter-box-label">Production Lines:</span>
                <div className="line-filters">
                  <label className="line-filter-item">
                    <input
                      type="checkbox"
                      checked={filterLines.includes("P1")}
                      onChange={() => toggleLineFilter("P1")}
                      className="line-checkbox checkbox-p1"
                      data-testid="filter-checkbox-p1"
                    />
                    <span className="line-label">P1</span>
                  </label>
                  <label className="line-filter-item">
                    <input
                      type="checkbox"
                      checked={filterLines.includes("P2")}
                      onChange={() => toggleLineFilter("P2")}
                      className="line-checkbox checkbox-p2"
                      data-testid="filter-checkbox-p2"
                    />
                    <span className="line-label">P2</span>
                  </label>
                  <label className="line-filter-item">
                    <input
                      type="checkbox"
                      checked={filterLines.includes("P3")}
                      onChange={() => toggleLineFilter("P3")}
                      className="line-checkbox checkbox-p3"
                      data-testid="filter-checkbox-p3"
                    />
                    <span className="line-label">P3</span>
                  </label>
                </div>
              </div>
              
              <div className="filter-box">
                <Label htmlFor="filter-category" className="filter-box-label">Category:</Label>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger id="filter-category" className="category-select" data-testid="filter-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.filter(cat => cat != null && cat !== "").map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <div className="days-grid-single">
            {weekDays.map(day => {
              const dayWOs = getWorkOrdersForDate(day);
              return (
                <DroppableDay key={format(day, "yyyy-MM-dd")} date={day}>
                  <div className="day-header">
                    <p className="day-name">{format(day, "EEE")}</p>
                    <p className="day-date">{format(day, "MMM d")}</p>
                  </div>
                  <div className="work-orders-list">
                    {dayWOs.length === 0 ? (
                      <div className="empty-day-clickable">
                        <Plus className="w-4 h-4" />
                        <span>Add work order</span>
                      </div>
                    ) : (
                      dayWOs.map(wo => (
                        <WorkOrderCard
                          key={wo.wo_id}
                          workOrder={wo}
                          onClick={() => handleWorkOrderClick(wo)}
                        />
                      ))
                    )}
                  </div>
                </DroppableDay>
              );
            })}
          </div>
        </div>
        
        <DragOverlay dropAnimation={null}>
          {activeId ? (
            <WorkOrderCard 
              workOrder={workOrders.find(wo => wo.wo_id === activeId)} 
              onClick={() => {}}
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Create Work Order Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="modal-content" data-testid="create-work-order-modal" aria-describedby="create-wo-description">
          <DialogHeader>
            <DialogTitle className="modal-title">Create New Work Order</DialogTitle>
            <p id="create-wo-description" className="sr-only">
              Fill in the form to create a new production work order
            </p>
          </DialogHeader>
          <div className="form-grid">
            <div>
              <Label htmlFor="production_line">Production Line *</Label>
              <Select 
                value={newWO.production_line} 
                onValueChange={(value) => setNewWO({...newWO, production_line: value})}
              >
                <SelectTrigger data-testid="production-line-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="P1">P1</SelectItem>
                  <SelectItem value="P2">P2</SelectItem>
                  <SelectItem value="P3">P3</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="item">Item *</Label>
              <Select 
                value={newWO.item_id} 
                onValueChange={(value) => setNewWO({...newWO, item_id: value})}
              >
                <SelectTrigger data-testid="item-select">
                  <SelectValue placeholder="Select item" />
                </SelectTrigger>
                <SelectContent>
                  {items.filter(item => item.item_id != null && String(item.item_id) !== "").map(item => (
                    <SelectItem key={item.item_id} value={String(item.item_id)}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="planned_quantity">Planned Quantity *</Label>
              <Input
                id="planned_quantity"
                type="number"
                value={newWO.planned_quantity}
                onChange={(e) => setNewWO({...newWO, planned_quantity: e.target.value})}
                placeholder="1000"
                data-testid="planned-quantity-input"
              />
            </div>
            <div>
              <Label htmlFor="planned_date">Date *</Label>
              <Input
                id="planned_date"
                type="date"
                value={newWO.planned_date}
                onChange={(e) => setNewWO({...newWO, planned_date: e.target.value})}
                data-testid="planned-date-input"
              />
            </div>
            <div>
              <Label htmlFor="start_time">Start Time *</Label>
              <Input
                id="start_time"
                type="time"
                value={newWO.planned_start_time}
                onChange={(e) => setNewWO({...newWO, planned_start_time: e.target.value})}
                data-testid="start-time-input"
              />
            </div>
            <div>
              <Label htmlFor="end_time">End Time *</Label>
              <Input
                id="end_time"
                type="time"
                value={newWO.planned_end_time}
                onChange={(e) => setNewWO({...newWO, planned_end_time: e.target.value})}
                data-testid="end-time-input"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={newWO.notes}
                onChange={(e) => setNewWO({...newWO, notes: e.target.value})}
                placeholder="Additional notes..."
                rows={3}
                data-testid="notes-textarea"
              />
            </div>
          </div>
          <div className="modal-actions">
            <Button variant="outline" onClick={() => setShowCreateModal(false)} data-testid="cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleCreateWorkOrder} data-testid="create-btn">
              Create Work Order
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Date Range Modal */}
      <Dialog open={showCustomDateModal} onOpenChange={setShowCustomDateModal}>
        <DialogContent className="modal-content max-w-md">
          <DialogHeader>
            <DialogTitle>Select Custom Date Range</DialogTitle>
          </DialogHeader>
          <div className="custom-date-form" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
            <div>
              <Label htmlFor="custom-start-date">Start Date</Label>
              <Input
                id="custom-start-date"
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                data-testid="custom-start-date-input"
              />
            </div>
            <div>
              <Label htmlFor="custom-end-date">End Date</Label>
              <Input
                id="custom-end-date"
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                data-testid="custom-end-date-input"
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <Button 
                variant="outline" 
                onClick={() => setShowCustomDateModal(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  setViewMode("custom");
                  setShowCustomDateModal(false);
                }}
                data-testid="apply-custom-dates-btn"
              >
                Apply
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Work Order Detail Modal */}
      {selectedWorkOrder && (
        <WorkOrderDetailModal
          open={showDetailModal}
          onOpenChange={setShowDetailModal}
          workOrder={selectedWorkOrder}
          items={items}
          onUpdate={handleUpdateWorkOrder}
          onDelete={handleDeleteWorkOrder}
          onClone={handleCloneWorkOrder}
        />
      )}
      </>
    } />
            <Route path="/schedule" element={<Navigate to="/" replace />} />
          </Routes>

        </main>
      </div>
    </div>
    </>
      )}
    </ProtectedRoute>
  );
}

export default App;